import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2, Sparkles, CheckCircle2, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/ist";
import { MiniMarkdown } from "../../lib/miniMarkdown";

const DEPT_ICONS = { general: "💬", orders: "📦", payments: "💳", projects: "🖥️", seo: "📈", support: "🛠️" };

function ActionBadge({ action }) {
  if (!action) return null;
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold" data-testid="chat-action-badge">
      <CheckCircle2 size={12} /> Work task created: {action.title}
    </div>
  );
}

function Message({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${isUser ? "bg-blue-600 text-white rounded-br-md" : "bg-slate-800/80 border border-slate-700 rounded-bl-md"}`}>
        {!isUser && (
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-1">
            <Bot size={11} /> Optivize AI {m.department && `· ${m.department}`}
          </div>
        )}
        {isUser ? <span className="whitespace-pre-wrap">{m.content}</span> : <MiniMarkdown content={m.content} />}
        {!isUser && <ActionBadge action={m.action} />}
      </div>
    </div>
  );
}

function Deliverables() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/portal/ai-work/outputs").then(r => setRows(r.data.rows)).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="card p-5 mt-6" data-testid="ai-deliverables-section">
      <h3 className="text-white font-bold flex items-center gap-2"><FileText size={16} className="text-emerald-400" /> AI Deliverables</h3>
      <p className="text-slate-500 text-xs mt-0.5">Work completed for you by Optivize AI agents.</p>
      <div className="mt-3 space-y-2">
        {rows.map(o => (
          <div key={o.id} className="rounded-xl border border-slate-700 bg-slate-800/50 overflow-hidden">
            <button onClick={() => setOpen(open === o.id ? null : o.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`deliverable-toggle-${o.id.slice(0, 6)}`}>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shrink-0">{o.type}</span>
              <span className="text-sm text-white font-semibold flex-1 truncate">{o.title}</span>
              <span className="text-[11px] text-slate-500 shrink-0">{fmtDateTime(o.created_at)}</span>
              {open === o.id ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
            </button>
            {open === o.id && (
              <div className="px-4 pb-4 border-t border-slate-700 pt-3 max-h-[420px] overflow-y-auto" data-testid="deliverable-content">
                <MiniMarkdown content={o.content} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AIAssistance() {
  const [depts, setDepts] = useState([]);
  const [dept, setDept] = useState("general");
  const [messages, setMessages] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/portal/ai-chat/departments").then(r => setDepts(r.data.departments)).catch(() => {});
    api.get("/portal/ai-chat/history?limit=100").then(r => setMessages(r.data.rows)).catch(() => setMessages([]));
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = useCallback(async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages(m => [...(m || []), { id: `tmp-${Date.now()}`, role: "user", content: msg, department: dept }]);
    try {
      const { data } = await api.post("/portal/ai-chat", { message: msg, department: dept });
      setMessages(m => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply, department: data.department_label, action: data.action_created }]);
      if (data.action_created) toast.success(`Task created for our team: ${data.action_created.title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI assistant is unavailable right now");
    } finally { setBusy(false); }
  }, [input, busy, dept]);

  return (
    <div data-testid="ai-assistance-page">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center"><Bot size={20} className="text-white" /></div>
        <div>
          <h1 className="text-xl font-extrabold text-white" style={{ fontFamily: "Outfit" }}>AI Agent Assistance</h1>
          <p className="text-slate-500 text-xs">Live chat with Optivize AI departments — they can execute work for you.</p>
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap mt-4" data-testid="chat-departments">
        {depts.map(d => (
          <button key={d.key} onClick={() => setDept(d.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${dept === d.key ? "bg-emerald-600 border-emerald-600 text-white" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:border-emerald-500/50"}`}
            data-testid={`chat-dept-${d.key}`}>
            <span className="mr-1">{DEPT_ICONS[d.key]}</span>{d.label}
          </button>
        ))}
      </div>

      <div className="card mt-4 flex flex-col" style={{ height: "58vh" }} data-testid="chat-window">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages === null ? (
            <Loader2 className="animate-spin mx-auto my-10 text-emerald-500" />
          ) : messages.length === 0 ? (
            <div className="text-center my-12">
              <Sparkles size={28} className="mx-auto text-emerald-500 mb-2" />
              <div className="text-white font-bold">How can our AI agents help you today?</div>
              <div className="text-slate-500 text-xs mt-1 max-w-sm mx-auto">Ask about your orders, payments, projects or SEO — or request actual work like a revision, and the AI will queue it for execution.</div>
              <div className="flex gap-2 flex-wrap justify-center mt-4">
                {["What's the status of my project?", "I need a revision on my website", "Show my recent payments"].map(s => (
                  <button key={s} onClick={() => setInput(s)} className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs hover:border-emerald-500/50" data-testid="chat-starter-prompt">{s}</button>
                ))}
              </div>
            </div>
          ) : messages.map(m => <Message key={m.id} m={m} />)}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-slate-800/80 border border-slate-700 rounded-2xl rounded-bl-md px-4 py-3 inline-flex items-center gap-2 text-sm text-slate-400" data-testid="chat-typing">
                <Loader2 size={14} className="animate-spin text-emerald-400" /> Optivize AI is working…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-slate-800 p-3 flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") send(); }}
            placeholder={`Message ${depts.find(d => d.key === dept)?.label || "AI"}…`}
            className="flex-1 bg-slate-800/80 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
            data-testid="chat-input" disabled={busy} />
          <button onClick={send} disabled={busy || !input.trim()}
            className="px-4 rounded-xl bg-emerald-600 text-white disabled:opacity-40 inline-flex items-center gap-1.5 text-sm font-bold"
            data-testid="chat-send-btn"><Send size={15} /> Send</button>
        </div>
      </div>

      <Deliverables />
    </div>
  );
}
